<script setup>
// Tela "Conectar o Claude": o Claude abre /oauth/authorize, o servidor confere o
// pedido e manda para cá com os mesmos parâmetros. O usuário escolhe a empresa e
// autoriza; o servidor devolve o endereço do Claude com o código (ou a recusa).
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import mcpOauthAPI from 'dashboard/api/mcpOauth';

const { t } = useI18n();
const route = useRoute();

const isLoading = ref(true);
const isSubmitting = ref(false);
const loadError = ref(false);
const clientName = ref('');
const redirectHost = ref('');
const accounts = ref([]);
const selectedAccountId = ref(null);

const request = computed(() => ({
  client_id: route.query.client_id,
  redirect_uri: route.query.redirect_uri,
  state: route.query.state,
  code_challenge: route.query.code_challenge,
  code_challenge_method: route.query.code_challenge_method,
  resource: route.query.resource,
}));

const allowedAccounts = computed(() => accounts.value.filter(a => a.allowed));

const capabilities = computed(() => [
  t('CLAUDE_OAUTH.CAN_READ'),
  t('CLAUDE_OAUTH.CAN_ACT'),
  t('CLAUDE_OAUTH.NEEDS_APPROVAL'),
]);

const blockedReason = account =>
  account.administrator
    ? t('CLAUDE_OAUTH.MODULE_REQUIRED')
    : t('CLAUDE_OAUTH.ADMIN_REQUIRED');

const load = async () => {
  try {
    const { data } = await mcpOauthAPI.details(request.value);
    clientName.value = data.client_name;
    redirectHost.value = data.redirect_host;
    accounts.value = data.accounts;
    selectedAccountId.value = allowedAccounts.value[0]?.id || null;
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const decide = async decision => {
  isSubmitting.value = true;
  try {
    const { data } = await mcpOauthAPI.decide({
      ...request.value,
      decision,
      account_id: selectedAccountId.value,
    });
    window.location.assign(data.redirect_url);
  } catch {
    loadError.value = true;
    isSubmitting.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div
    class="flex min-h-screen items-center justify-center bg-n-background p-4"
  >
    <div
      class="flex w-full max-w-md flex-col gap-5 rounded-2xl border border-n-weak bg-n-solid-2 p-6"
    >
      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner />
      </div>

      <template v-else-if="loadError">
        <h1 class="m-0 text-lg font-semibold text-n-slate-12">
          {{ $t('CLAUDE_OAUTH.ERROR_TITLE') }}
        </h1>
        <p class="m-0 text-sm text-n-slate-11">
          {{ $t('CLAUDE_OAUTH.ERROR_BODY') }}
        </p>
      </template>

      <template v-else>
        <div class="flex flex-col gap-1">
          <h1 class="m-0 text-lg font-semibold text-n-slate-12">
            {{ $t('CLAUDE_OAUTH.TITLE', { app: clientName }) }}
          </h1>
          <p class="m-0 text-sm text-n-slate-11">
            {{ $t('CLAUDE_OAUTH.SUBTITLE', { host: redirectHost }) }}
          </p>
        </div>

        <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
          <legend class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('CLAUDE_OAUTH.ACCOUNT') }}
          </legend>
          <label
            v-for="account in accounts"
            :key="account.id"
            class="flex items-start gap-3 rounded-lg border border-n-weak p-3"
            :class="account.allowed ? 'cursor-pointer' : 'opacity-60'"
          >
            <input
              v-model="selectedAccountId"
              type="radio"
              name="account"
              :value="account.id"
              :disabled="!account.allowed"
              class="mt-1"
            />
            <span class="flex flex-col">
              <span class="text-sm text-n-slate-12">{{ account.name }}</span>
              <span v-if="!account.allowed" class="text-xs text-n-slate-10">
                {{ blockedReason(account) }}
              </span>
            </span>
          </label>
        </fieldset>

        <ul class="m-0 flex flex-col gap-1 pl-5 text-sm text-n-slate-11">
          <li v-for="item in capabilities" :key="item">{{ item }}</li>
        </ul>

        <div class="flex justify-end gap-2">
          <Button
            :label="$t('CLAUDE_OAUTH.DENY')"
            variant="faded"
            color="slate"
            :disabled="isSubmitting"
            @click="decide('deny')"
          />
          <Button
            :label="$t('CLAUDE_OAUTH.ALLOW')"
            :disabled="!selectedAccountId"
            :is-loading="isSubmitting"
            @click="decide('allow')"
          />
        </div>
      </template>
    </div>
  </div>
</template>
